import { useCallback, useState } from 'react';

/**
 * `useState` that survives a page reload, for the rest of the browser session.
 *
 * Written for the list toolbars on My Office: an admin who filters to one office, opens a
 * record and comes back — or just refreshes — used to land on an unfiltered list and have to
 * set it up again. The state itself is throwaway UI preference, so it is kept in
 * `sessionStorage` rather than on the server: it outlives a reload and dies with the tab,
 * which is what "for this session" means to the person using it. Nothing here is shared
 * between tabs, so two tabs can hold different filters at once.
 *
 * Only JSON-serialisable values. Everything stored through this today is a string.
 */

/** Namespaced so a key cannot collide with anything else the app puts in session storage. */
const KEY_PREFIX = 'whyatwork.ui.';

/**
 * Session storage is not always there to be used: Safari's private mode has historically
 * thrown on write, and browsers can disable storage entirely. None of this is worth failing
 * a render over, so every access degrades to plain in-memory state.
 */
const readStored = <T>(storageKey: string, fallback: T): T => {
  try {
    const raw = window.sessionStorage.getItem(KEY_PREFIX + storageKey);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
};

const writeStored = (storageKey: string, value: unknown, isDefault: boolean): void => {
  try {
    if (isDefault) {
      // A value back at its default carries no information, and leaving it behind would
      // make "has this admin filtered anything?" a question about storage rather than state.
      window.sessionStorage.removeItem(KEY_PREFIX + storageKey);
      return;
    }

    window.sessionStorage.setItem(KEY_PREFIX + storageKey, JSON.stringify(value));
  } catch {
    // Storage unavailable or full. The state still updates; it just will not outlive
    // the reload.
  }
};

/**
 * @param storageKey Stable and unique per piece of state, e.g. `my-office.offices.search`.
 * @param defaultValue Used when nothing is stored, and what the value is compared against
 *                     to decide whether it is worth storing at all.
 */
export const useSessionState = <T>(
  storageKey: string,
  defaultValue: T,
): [T, (value: T) => void] => {
  // Read once, on mount. Re-reading on every render would fight with the setter and make
  // the stored copy, rather than React, the source of truth.
  const [value, setValue] = useState<T>(() => readStored(storageKey, defaultValue));

  const set = useCallback(
    (next: T): void => {
      setValue(next);
      writeStored(storageKey, next, JSON.stringify(next) === JSON.stringify(defaultValue));
    },
    // defaultValue is compared by value, so a caller passing a fresh object literal each
    // render does not need to memoise it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey, JSON.stringify(defaultValue)],
  );

  return [value, set];
};

export default useSessionState;
